export {}

// Keep the decorative video controllable, and decode only while it is on screen.
let dispose: (() => void) | undefined

function init() {
  dispose?.()
  dispose = undefined
  const video = document.querySelector<HTMLVideoElement>('#home-background-video')
  const button = document.querySelector<HTMLButtonElement>('[data-hero-video-control]')
  if (!video || !button) return
  const motion = matchMedia('(prefers-reduced-motion: reduce)')
  const events = new AbortController()
  const { signal } = events
  let wanted = !motion.matches, visible = false, disposed = false, pending = false, ready = false
  let idle: number | undefined, fallback: ReturnType<typeof setTimeout> | undefined
  const shouldPlay = () => ready && wanted && visible && !document.hidden && !disposed
  const updateLabel = () => { button.textContent = wanted ? 'Video pauzeren' : 'Video afspelen' }

  function cancelStartup() {
    window.removeEventListener('load', queueStartup)
    if (idle !== undefined) window.cancelIdleCallback?.(idle)
    clearTimeout(fallback)
    idle = fallback = undefined
  }
  function start() {
    cancelStartup()
    if (disposed) return
    ready = true
    sync()
  }
  function queueStartup() {
    // Give the poster, fonts and form their first load before fetching/decoding
    // decorative media. Safari's timer fallback follows the same load gate.
    if (ready || disposed) return
    if (typeof window.requestIdleCallback === 'function') idle = window.requestIdleCallback(start, { timeout: 2000 })
    else fallback = setTimeout(start, 200)
  }

  function sync() {
    updateLabel()
    if (!shouldPlay()) { video!.pause(); return }
    if (pending || !video!.paused) return
    if (!video!.hasAttribute('src')) {
      video!.src = video!.dataset.src!
      video!.load()
    }
    // Muting the property as well as the attribute is needed after Astro navigation.
    video!.muted = true
    pending = true
    video!.play().catch(error => {
      if (disposed || error.name === 'AbortError') return
      wanted = false
      updateLabel()
    }).finally(() => {
      pending = false
      if (shouldPlay() && video!.paused) sync()
    })
  }

  button.hidden = false
  button.addEventListener('click', () => {
    wanted = !wanted
    // A visitor explicitly asking to play should not wait for automatic startup.
    if (wanted) start()
    else sync()
  }, { signal })
  video.addEventListener('playing', () => {
    button.hidden = false
    if (!shouldPlay()) video.pause()
  }, { signal })
  video.addEventListener('error', () => { button.hidden = true }, { signal })
  document.addEventListener('visibilitychange', sync, { signal })
  motion.addEventListener('change', () => { wanted = !motion.matches; sync() }, { signal })
  const observer = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting
    sync()
  })
  observer.observe(video)
  updateLabel()
  if (document.readyState === 'complete') queueStartup()
  else window.addEventListener('load', queueStartup, { once: true, signal })
  dispose = () => {
    disposed = true
    cancelStartup()
    observer.disconnect()
    events.abort()
    video.pause()
  }
}

init()
document.addEventListener('astro:page-load', init)
document.addEventListener('astro:before-swap', () => { dispose?.(); dispose = undefined })
